import { useState } from 'react';
import { Outlet, Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';
import { esAdmin, esBroker } from '@/lib/roles';
import NotificationBell from '@/components/NotificationBell';
import { 
  Ship, LayoutDashboard, Key, Package, Shield, 
  Receipt, Menu, X, LogOut, ChevronRight, Plus,
  FileText, Truck, Zap, Building2, ShieldCheck,
  Settings, Crown, Camera, Radio, DollarSign, Activity, Bell, BookOpen, Wand2, Wallet, BadgeCheck, BarChart3,
  Scale
} from 'lucide-react';

const DashboardLayout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const isAdmin = esAdmin(user);
  const isBrokerOrInsurer = esBroker(user);

  // Brokers / insurers get their own minimal menu — no shipping tools
  const brokerNavItems = [
    { path: '/dashboard/broker/report', label: 'Insurance Report', icon: Shield },
    { path: '/dashboard/insurance-reporting', label: 'Coverage Ledger', icon: FileText },
    { path: '/dashboard/settings/security', label: 'Security (2FA)', icon: ShieldCheck },
  ];

  const merchantNavItems = [
    { path: '/dashboard', label: 'Overview', icon: LayoutDashboard },
    { path: '/dashboard/rate-shop', label: 'Rate Shop', icon: Truck },
    { path: '/dashboard/sellegard', label: 'SelleGard Freight', icon: Truck },
    { path: '/dashboard/activity', label: 'Shipping Activity', icon: Activity },
    { path: '/dashboard/tracking', label: 'Tracking & Alerts', icon: Bell },
    { path: '/dashboard/billing', label: 'Billing Statement', icon: Receipt },
    { path: '/dashboard/sellecheck', label: 'SelleCheck', icon: BadgeCheck },
    { path: '/dashboard/premium-tools', label: 'Premium Tools', icon: Radio },
    { path: '/dashboard/click-pic', label: 'Click Pic', icon: Camera },
    { path: '/dashboard/protect', label: 'SelleSend Protect', icon: ShieldCheck },
    { path: '/dashboard/selletrans', label: 'SelleSend (Parcel Risk)', icon: FileText },
    { path: '/dashboard/selle10', label: 'Selle10 · Up to $25k', icon: ShieldCheck },
    { path: '/dashboard/elite', label: 'SelleGuard Elite · $1M+', icon: Crown },
    { path: '/dashboard/programs', label: 'Programs · RiskChoice', icon: Shield },
    { path: '/dashboard/selleshop', label: 'SelleShop · Publish', icon: FileText },
    { path: '/dashboard/claims', label: 'Claims', icon: FileText },
    { path: '/dashboard/digest', label: 'Daily Digest', icon: Radio },
    { path: '/dashboard/sell', label: 'Selling Hub', icon: DollarSign },
    { path: '/dashboard/shipments', label: 'Shipments', icon: Package },
    { path: '/dashboard/sameday', label: 'Instant Delivery', icon: Zap },
    { path: '/dashboard/office-network', label: 'Office Network', icon: Building2 },
    { path: '/dashboard/insurance', label: 'Insurance', icon: FileText },
    { path: '/dashboard/verification', label: 'Verification', icon: Shield },
    { path: '/verify', label: 'KYS/AML Wizard', icon: ShieldCheck },
    { path: '/dashboard/wallet', label: 'Wallet', icon: Wallet },
    { path: '/dashboard/coverage-terms', label: 'Coverage Terms', icon: ShieldCheck },
    { path: '/dashboard/transactions', label: 'Transactions', icon: Receipt },
    { path: '/dashboard/legal', label: 'Legal Center', icon: FileText },
    { path: '/dashboard/settings/security', label: 'Security (2FA)', icon: ShieldCheck },
  ];

  // Merchants never see these — reserved for platform admins only
  const adminNavItemsCompletos = [
    { path: '/dashboard/admin', label: 'Admin Dashboard', icon: Settings },
    { path: '/dashboard/admin/setup-wizard', label: 'Setup Wizard', icon: Wand2 },
    { path: '/dashboard/platform-admin', label: 'Platform Admin', icon: Crown },
    { path: '/dashboard/admin/sellegard-config', label: 'Carrier Config', icon: Truck },
    { path: '/dashboard/admin/sellegard-credit', label: 'Merchant Credit', icon: Receipt },
    { path: '/dashboard/admin/revenue', label: 'Revenue', icon: DollarSign },
    { path: '/dashboard/admin/metrics', label: 'Business Metrics', icon: BarChart3 },
    { path: '/dashboard/admin/aml-documents', label: 'AML Documents', icon: FileText },
    { path: '/dashboard/admin/health', label: 'Integration Health', icon: Activity },
    { path: '/dashboard/admin/reconciliation', label: 'Reconciliation', icon: Scale },
    { path: '/dashboard/admin/programs', label: 'Rate Editor', icon: Settings },
    { path: '/dashboard/admin/passwords', label: 'Passwords', icon: Key },
    { path: '/dashboard/admin/merchants', label: 'Merchants', icon: Building2 },
    { path: '/dashboard/admin/addresses', label: 'Address Books', icon: BookOpen },
    { path: '/dashboard/admin/coverage-terms', label: 'Coverage Terms', icon: FileText },
    { path: '/dashboard/insurance-reporting', label: 'Insurance Reporting', icon: FileText },
    { path: '/dashboard/admin/kys', label: 'KYS Review Queue', icon: ShieldCheck },
    { path: '/dashboard/admin/shipstation-accounts', label: 'ShipStation Accounts', icon: Truck },
    { path: '/dashboard/admin/reports/loss-ratio', label: 'Loss Ratio', icon: DollarSign },
    { path: '/dashboard/admin/reports/carrier-pnl', label: 'Carrier P&L', icon: Truck },
    { path: '/dashboard/admin/riskchoice-audit', label: 'RiskChoice Audit', icon: FileText },
    { path: '/dashboard/broker/report', label: 'Insurance Broker Report', icon: Shield },
    { path: '/dashboard/secrets', label: 'API Secrets', icon: Key },
  ];
  const adminNavItems = isAdmin ? adminNavItemsCompletos : [];

  // Lanzamiento del 26-oct, como lo decidio Mike el 24-oct (RESPUESTAS_MIKE.md
  // §9.121): lo que todavia no esta verificado no aparece en el menu del
  // comerciante, salvo Selle10, que pidio dejar. MultiSend salio del menu
  // ("Sendle is gone"). No se borra nada: las rutas siguen, el admin lo ve y
  // cada opcion vuelve al menu cuando se verifica.
  const SIN_VERIFICAR = new Set([
    '/dashboard/premium-tools', '/dashboard/click-pic', '/dashboard/protect',
    '/dashboard/elite', '/dashboard/programs', '/dashboard/selleshop',
    '/dashboard/digest', '/dashboard/sell', '/dashboard/sameday',
    '/dashboard/office-network', '/dashboard/insurance', '/dashboard/verification',
    '/dashboard/tracking',
  ]);

  const navItems = isBrokerOrInsurer
    ? brokerNavItems
    : merchantNavItems.filter((item) => isAdmin || !SIN_VERIFICAR.has(item.path));

  // Sacarlo del menu no alcanzaba: un comerciante que escribia la direccion (o
  // tenia un enlace guardado) entraba igual a Premium Tools, Verification o API
  // Secrets (entrega 52, RESPUESTAS_MIKE.md §9.159). Lo oculto y lo del admin lo
  // mandan al Overview. El admin y el broker siguen igual.
  const rutasCerradas = [...SIN_VERIFICAR, ...adminNavItemsCompletos.map((item) => item.path), '/dashboard/admin'];
  const cerradaParaComerciante = rutasCerradas.some(
    (p) => location.pathname === p || location.pathname.startsWith(`${p}/`));
  if (user && !isAdmin && !isBrokerOrInsurer && cerradaParaComerciante) {
    return <Navigate to="/dashboard" replace />;
  }

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const isActive = (path) => {
    if (path === '/dashboard') {
      return location.pathname === '/dashboard';
    }
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
      {/* Mobile header */}
      <header className="lg:hidden glass sticky top-0 z-50 border-b border-slate-200 dark:border-slate-800">
        <div className="flex items-center justify-between px-4 py-3">
          <Link to="/dashboard" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center overflow-hidden">
              <img src="/assets/logo/sellesend-logo.png" alt="SelleSend" className="w-full h-full object-contain" />
            </div>
            <span className="text-lg font-semibold">SelleSend</span>
          </Link>
          <div className="flex items-center gap-2 lg:hidden" data-testid="mobile-notif-slot">
            <NotificationBell />
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              data-testid="mobile-menu-btn"
            >
              {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </Button>
          </div>
        </div>
      </header>

      {/* Desktop notification bell lives inside the sidebar user section (below) */}

      <div className="flex">
        {/* Sidebar */}
        <aside 
          className={`
            fixed lg:sticky top-0 left-0 z-40 h-screen w-64 
            bg-white dark:bg-slate-900 border-r border-slate-200 dark:border-slate-800
            transform transition-transform duration-200 ease-in-out
            lg:translate-x-0
            ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
          `}
        >
          <div className="flex flex-col h-full">
            {/* Logo */}
            <div className="hidden lg:flex items-center gap-2 px-6 py-5 border-b border-slate-200 dark:border-slate-800">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center overflow-hidden">
                <img src="/assets/logo/sellesend-logo.png" alt="SelleSend" className="w-full h-full object-contain" />
              </div>
              <span className="text-xl font-semibold tracking-tight">SelleSend</span>
            </div>

            {/* Navigation */}
            <nav className="flex-1 px-4 py-6 space-y-1 overflow-y-auto">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  className={`sidebar-link ${isActive(item.path) ? 'active' : ''}`}
                  data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                >
                  <item.icon className="w-5 h-5" />
                  <span>{item.label}</span>
                </Link>
              ))}

              {/* Admin Section — only visible to platform admins */}
              {isAdmin && adminNavItems.length > 0 && (
                <div className="pt-4 mt-4 border-t border-slate-200 dark:border-slate-700">
                  <p className="px-3 text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Admin</p>
                  {adminNavItems.map((item) => (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={() => setSidebarOpen(false)}
                      className={`sidebar-link ${isActive(item.path) ? 'active' : ''}`}
                      data-testid={`nav-${item.label.toLowerCase().replace(/ /g, '-')}`}
                    >
                      <item.icon className="w-5 h-5" />
                      <span>{item.label}</span>
                    </Link>
                  ))}
                </div>
              )}

              <div className="pt-4">
                {!isBrokerOrInsurer && (
                  <Link to="/dashboard/rate-shop" onClick={() => setSidebarOpen(false)}>
                    <Button className="w-full btn-press" data-testid="new-shipment-btn">
                      <Plus className="w-4 h-4 mr-2" />
                      New Shipment
                    </Button>
                  </Link>
                )}
              </div>
            </nav>

            {/* User section */}
            <div className="p-4 border-t border-slate-200 dark:border-slate-800">
              {/* Contact Info */}
              <div className="mb-4 p-3 bg-slate-50 dark:bg-slate-900 rounded-lg text-xs">
                <p className="font-medium mb-1">Support</p>
                <p className="text-slate-500">(619) 930-1074</p>
                <p className="text-slate-400 mt-1">24/7 AI: (510) 422-4451</p>
              </div>
              
              <div className="flex items-center justify-between mb-4">
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">{user?.company_name}</p>
                  <p className="text-sm text-slate-500 truncate">{user?.email}</p>
                </div>
                <div className="hidden lg:block flex-shrink-0 ml-2">
                  <NotificationBell />
                </div>
              </div>
              <Button 
                variant="ghost" 
                className="w-full justify-start text-slate-500 hover:bg-red-500 hover:text-white"
                onClick={handleLogout}
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Sign out
              </Button>
            </div>
          </div>
        </aside>

        {/* Overlay for mobile */}
        {sidebarOpen && (
          <div 
            className="fixed inset-0 bg-black/50 z-30 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Main content */}
        <main className="flex-1 min-w-0 min-h-screen lg:min-h-[calc(100vh)]">
          <div className="p-4 md:p-8">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;
